import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { getBrandCampaigns, getSelectedCampaign } from '../../../store/selector/campaign.selector';
import {
    ICampaignDeliverables,
    ICreateCampaignRequestPayload,
    IRequestCampaignDeliverable,
    IUpdateCampaignRequestPayload,
    IWriteCampaignDeliverable,
    IWriteCampaignRequestPayload,
} from '../../../interfaces/campaign.interface';
import { useGetOrganizationTemplatesQuery } from '../../../store/api/template.api';
import { getActiveBrand } from '../../../store/selector/brand.selector';
import { getOrganizationId } from '../../../store/selector/organization.selector';
import { DeliverableTask } from '../../../constants/campaign';
import { IGenerateCampaignRequestPayload } from '../../../pages/Apprentice/apprentice.interface';
import { getCurrentlyWorkingSession } from '../../../store/selector/session.selector';
import { useGenerateCampaignMutation } from '../../../store/api/apprentice.api';
import {
    displayErrorToast,
    displaySuccessToast,
    displayWarningToast,
} from '../../../utils/ToastUtils';
import { SetCurrentlyWorkingSession, UpdateSessionStage } from '../../../store/slice/session.slice';
import { SessionStage } from '../../../interfaces/session.interface';
import { getCampaignContent } from '../../../utils/campaignUtils';
import {
    useCreateCampaignMutation,
    useUpdateCampaignMutation,
    useWriteCampaignMutation,
} from '../../../store/api/campaign.api';
import { AddBrandCampaign, SetSelectedCampaign } from '../../../store/slice/campaign.slice';
import { setTemplateDetailsInCampaign } from '../../../utils/campaign.utils';
import { Button, Form, Spinner } from 'react-bootstrap';
import CampaignTextArea from '../extras/DeliverableTextArea';
import DeliverableSection from './DeliverableSection';
import { CampaignTab } from '../../../constants/common';
import { SetCampaignAds } from '../../../store/slice/campaign-ads.slice';
import { getTemplateFromId } from '../../../utils/template.util';
import {
    getAdFromUrlActive,
    getAdFromUrlLoading,
} from '../../../store/selector/ad-from-url.selector';
import { useMarkUserOldMutation } from '../../../store/api/user.api';
import { SetUserOld } from '../../../store/slice/user.slice';
import { SetAdFromUrlFlowInactive } from '../../../store/slice/ad-from-url.slice';

interface IProps {
    generatingCampaign: boolean;
    setGeneratingCampaign: React.Dispatch<React.SetStateAction<boolean>>;
    setIsSwitching: React.Dispatch<React.SetStateAction<boolean>>;
    setActiveTab: React.Dispatch<React.SetStateAction<CampaignTab>>;
}

const PlanEdit = ({
    generatingCampaign,
    setGeneratingCampaign,
    setIsSwitching,
    setActiveTab,
}: IProps) => {
    const dispatch = useAppDispatch();
    const selectedCampaign = useAppSelector(getSelectedCampaign);
    const activeBrand = useAppSelector(getActiveBrand);
    const organizationId = useAppSelector(getOrganizationId);
    const brandCampaigns = useAppSelector(getBrandCampaigns);
    const adFromUrlActive = useAppSelector(getAdFromUrlActive);
    const adFromUrlLoading = useAppSelector(getAdFromUrlLoading);
    const currentlyWorkingSession = useAppSelector(getCurrentlyWorkingSession);
    const [generateCampaign] = useGenerateCampaignMutation();
    const [writeCampaign, { isLoading: writeCampaignLoading }] = useWriteCampaignMutation();
    const [createCampaign, { isLoading: createCampaignLoading }] = useCreateCampaignMutation();
    const [updateCampaign, { isLoading: updateCampaignLoading }] = useUpdateCampaignMutation();
    const [markUserOld] = useMarkUserOldMutation();
    const { data: organizationTemplates } = useGetOrganizationTemplatesQuery(
        organizationId ? organizationId : 0,
        { skip: !organizationId },
    );

    const [campaignWriterText, setCampaignWriterText] = useState('');
    const [campaignName, setCampaignName] = useState(
        !selectedCampaign ? 'New Campaign' : selectedCampaign.name,
    );
    const [objective, setObjective] = useState(!selectedCampaign ? '' : selectedCampaign.objective);
    const [targetAudience, setTargetAudience] = useState(
        !selectedCampaign ? '' : selectedCampaign.target_audience,
    );
    const [keyMessages, setKeyMessages] = useState(
        !selectedCampaign ? '' : selectedCampaign.key_message,
    );
    const [otherDetails, setOtherDetails] = useState(
        !selectedCampaign
            ? ''
            : selectedCampaign.other_details
              ? selectedCampaign.other_details
              : '',
    );
    const [isEditingObjective, setIsEditingObjective] = useState(false);
    const [isEditingTargetAudience, setIsEditingTargetAudience] = useState(false);
    const [isEditingKeyMessages, setIsEditingKeyMessages] = useState(false);
    const [isEditingOtherDetails, setIsEditingOtherDetails] = useState(false);
    const [isEditingCampaignName, setIsEditingCampaignName] = useState(false);

    const [deliverables, setDeliverables] = useState<ICampaignDeliverables[]>(
        !selectedCampaign ? [] : selectedCampaign.deliverables,
    );

    const docInputRef = useRef<HTMLInputElement>(null);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isCampaignDetailsCollapsed, setIsCampaignDetailsCollapsed] = useState(
        selectedCampaign ? true : false,
    );
    const deliverableSectionRef = useRef<HTMLDivElement>(null);

    const handleCampaignNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCampaignName(e.target.value);
    };

    const handleCampaignNameBlur = () => {
        setIsEditingCampaignName(false);
    };

    const handleCampaignNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setIsEditingCampaignName(false);
        }
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        onSubmit: () => void,
    ) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
        }
    };

    const handlePDFupload = () => {
        if (docInputRef.current) {
            docInputRef.current.click();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            setUploadedFile(file);
        }
    };

    const handleRemoveDoc = () => {
        setUploadedFile(null);
    };

    const toggleCampaignDetailsCollapsed = () => {
        setIsCampaignDetailsCollapsed(!isCampaignDetailsCollapsed);
    };

    const newCampaignPreTask = () => {
        setCampaignName('New Campaign');
        setObjective('');
        setTargetAudience('');
        setKeyMessages('');
        setOtherDetails('');
        setDeliverables([]);
        setIsEditingObjective(false);
        setIsEditingTargetAudience(false);
        setIsEditingKeyMessages(false);
        setIsEditingOtherDetails(false);
        setIsEditingCampaignName(false);
    };

    const oldCampaignPreTask = () => {
        if (selectedCampaign) {
            setCampaignName(selectedCampaign.name);
            setObjective(selectedCampaign.objective);
            setTargetAudience(selectedCampaign.target_audience);
            setKeyMessages(selectedCampaign.key_message);
            setOtherDetails(selectedCampaign.other_details ? selectedCampaign.other_details : '');
            setDeliverables(selectedCampaign.deliverables);
            setIsEditingObjective(false);
            setIsEditingTargetAudience(false);
            setIsEditingKeyMessages(false);
            setIsEditingOtherDetails(false);
            setIsEditingCampaignName(false);
        }
    };

    const isCampaignEdited = () => {
        if (!selectedCampaign) return false;
        if (
            campaignName !== selectedCampaign.name ||
            objective !== selectedCampaign.objective ||
            targetAudience !== selectedCampaign.target_audience ||
            keyMessages !== selectedCampaign.key_message
        ) {
            return true;
        }
        if (selectedCampaign.other_details && otherDetails !== selectedCampaign.other_details) {
            return true;
        }
        if (deliverables.length !== selectedCampaign.deliverables.length) {
            return true;
        }
        for (let i = 0; i < deliverables.length; i++) {
            if (
                deliverables[i].asset_type !== selectedCampaign.deliverables[i].asset_type ||
                deliverables[i].platform !== selectedCampaign.deliverables[i].platform ||
                deliverables[i].content !== selectedCampaign.deliverables[i].content ||
                deliverables[i].deliverable_number !==
                    selectedCampaign.deliverables[i].deliverable_number ||
                deliverables[i].task !== selectedCampaign.deliverables[i].task
            ) {
                return true;
            }

            if (
                deliverables[i].sub_deliverable &&
                selectedCampaign.deliverables[i].sub_deliverable &&
                deliverables[i].sub_deliverable !== selectedCampaign.deliverables[i].sub_deliverable
            ) {
                return true;
            }

            if (deliverables[i].templateDetails === null) {
                if (selectedCampaign.deliverables[i].template) return true;
            } else {
                if (
                    deliverables[i].templateDetails?.id !==
                    selectedCampaign.deliverables[i].template
                )
                    return true;
            }
            if (
                deliverables[i].user_product_path === '' ||
                deliverables[i].user_product_path === null ||
                deliverables[i].user_product_path === undefined
            ) {
                if (
                    selectedCampaign.deliverables[i].user_product_path &&
                    selectedCampaign.deliverables[i].user_product_path !== ''
                )
                    return true;
            } else {
                if (
                    deliverables[i].user_product_path !==
                    selectedCampaign.deliverables[i].user_product_path
                )
                    return true;
            }
            if (
                deliverables[i].ad_product_photo === null ||
                deliverables[i].ad_product_photo === undefined
            ) {
                if (selectedCampaign.deliverables[i].ad_product_photo) return true;
            } else {
                if (
                    deliverables[i].ad_product_photo !==
                    selectedCampaign.deliverables[i].ad_product_photo
                )
                    return true;
            }
        }
        return false;
    };

    const validateCampaign = () => {
        if (
            campaignName.length === 0 ||
            objective.length === 0 ||
            targetAudience.length === 0 ||
            keyMessages.length === 0
        ) {
            return false;
        }
        for (let i = 0; i < deliverables.length; i++) {
            if (
                deliverables[i].asset_type?.length === 0 ||
                deliverables[i].platform?.length === 0 ||
                (deliverables[i].task != DeliverableTask.RESIZE &&
                    deliverables[i].content.length === 0) ||
                deliverables[i].task.length === 0
            ) {
                return false;
            }
        }

        return true;
    };

    const approveAndGenerateCampaign = async () => {
        if (generatingCampaign) {
            displayWarningToast('Campaign generation already in progress');
            return;
        }
        if (selectedCampaign && activeBrand && organizationId && currentlyWorkingSession) {
            dispatch(SetCampaignAds({ ads: [], sessionKey: null }));
            setGeneratingCampaign(true);

            let productPhotoAttached = true;
            deliverables.forEach((deliverable) => {
                if (
                    deliverable.task === DeliverableTask.PRODUCT_PHOTOGRAPHY &&
                    (!deliverable.user_product_path || deliverable.user_product_path === '')
                ) {
                    productPhotoAttached = false;
                }
            });

            if (!productPhotoAttached) {
                displayErrorToast('Product photo is required to generate a campaign');
                setGeneratingCampaign(false);
                return;
            }

            setActiveTab(CampaignTab.CREATE);

            const campaignTemplateIds: string[] = [];
            const campaignUserProductPaths: string[] = [];
            const tagsList: string[][] = [];

            deliverables.forEach((deliverable) => {
                if (deliverable.tags) {
                    tagsList.push(deliverable.tags);
                } else {
                    tagsList.push([]);
                }

                if (deliverable.sub_deliverable) {
                    campaignUserProductPaths.push('');
                    campaignTemplateIds.push('');
                } else {
                    if (deliverable.user_product_path && deliverable.user_product_path.length > 0) {
                        campaignUserProductPaths.push(deliverable.user_product_path);
                    } else campaignUserProductPaths.push('');

                    if (
                        deliverable.templateDetails &&
                        deliverable.templateDetails.preview_url.length > 0
                    ) {
                        campaignTemplateIds.push(deliverable.templateDetails.id);
                    } else campaignTemplateIds.push('');
                }
            });

            const generateCampaignRequestPayload: IGenerateCampaignRequestPayload = {
                prompt: 'Approve and Generate',
                sessionId: currentlyWorkingSession.sessionKey,
                campaignId: selectedCampaign.id,
                campaignUserProductPaths: campaignUserProductPaths,
                campaignTemplateNames: campaignTemplateIds,
                brandId: activeBrand.id,
                organizationId: organizationId,
                tags: tagsList,
            };

            dispatch(
                SetCurrentlyWorkingSession({
                    ...currentlyWorkingSession,
                    stage: SessionStage.CREATE,
                }),
            );
            dispatch(
                UpdateSessionStage({
                    sessionKey: currentlyWorkingSession.sessionKey,
                    stage: SessionStage.CREATE,
                }),
            );

            try {
                const campaignResponse: any = await generateCampaign(
                    generateCampaignRequestPayload,
                );
                const success = campaignResponse && campaignResponse.data;
                setGeneratingCampaign(false);
                if (!success) {
                    displayErrorToast("Couldn't generate campaign. Please try again.");
                } else {
                    displaySuccessToast('Your campaign is ready');
                    if (adFromUrlActive) {
                        await markUserOld();
                        dispatch(SetUserOld());
                    }
                }
                dispatch(SetAdFromUrlFlowInactive());
            } catch (e) {
                dispatch(SetAdFromUrlFlowInactive());
                displayErrorToast("Couldn't generate campaign. Please try again.");
            }
        }
    };

    const handleSaveCampaign = async () => {
        if (!activeBrand) return;
        if (generatingCampaign) {
            displayWarningToast('Campaign generation already in progress');
            return;
        }
        if (!validateCampaign()) {
            displayErrorToast('Validation failed. Please fill in all required fields.');
            return;
        }
        try {
            if (!selectedCampaign) {
                const payloadDeliverables: IRequestCampaignDeliverable[] = [];
                for (let i = 0; i < deliverables.length; i++) {
                    const deliverableToBePushed: IRequestCampaignDeliverable = {
                        assetType: deliverables[i].asset_type,
                        platform: deliverables[i].platform,
                        content: getCampaignContent(deliverables[i]),
                        deliverableNumber: deliverables[i].deliverable_number,
                        subDeliverable: deliverables[i].sub_deliverable,
                        task: deliverables[i].task,
                        tags: deliverables[i].tags,
                    };
                    if (deliverables[i].templateDetails) {
                        deliverableToBePushed.templateId = deliverables[i].templateDetails?.id;
                    }
                    if (
                        deliverables[i].user_product_path &&
                        deliverables[i].user_product_path !== ''
                    ) {
                        deliverableToBePushed.userProductPath = deliverables[i].user_product_path;
                        deliverableToBePushed.adProductPhoto = deliverables[i].ad_product_photo
                            ? true
                            : false;
                    }
                    payloadDeliverables.push(deliverableToBePushed);
                }

                const payload: ICreateCampaignRequestPayload = {
                    name: campaignName,
                    objective: objective,
                    targetAudience: targetAudience,
                    keyMessage: keyMessages,
                    deliverables: payloadDeliverables,
                    brandId: activeBrand.id,
                };
                if (otherDetails.length > 0) {
                    payload.otherDetails = otherDetails;
                }

                const response = await createCampaign(payload);
                if (response && 'data' in response) {
                    const data = response.data;
                    dispatch(
                        AddBrandCampaign(
                            setTemplateDetailsInCampaign(
                                data,
                                organizationTemplates ? organizationTemplates : [],
                            ),
                        ),
                    );
                    dispatch(
                        SetSelectedCampaign(
                            setTemplateDetailsInCampaign(
                                data,
                                organizationTemplates ? organizationTemplates : [],
                            ),
                        ),
                    );
                }
                displaySuccessToast('Campaign saved successfully.');
            }
        } catch (error) {
            displayErrorToast('Failed to save the campaign. Please try again.');
        }
    };

    const handleUpdateCampaign = async () => {
        if (!activeBrand) return;
        if (generatingCampaign) {
            displayWarningToast('Campaign generation already in progress');
            return;
        }
        if (!validateCampaign()) {
            displayErrorToast('Validation failed. Please fill in all required fields.');
            return;
        }
        try {
            if (selectedCampaign && selectedCampaign.id) {
                const payloadDeliverables: IRequestCampaignDeliverable[] = [];
                for (let i = 0; i < deliverables.length; i++) {
                    const deliverableToBePushed: IRequestCampaignDeliverable = {
                        assetType: deliverables[i].asset_type,
                        platform: deliverables[i].platform,
                        content: getCampaignContent(deliverables[i]),
                        deliverableNumber: deliverables[i].deliverable_number,
                        subDeliverable: deliverables[i].sub_deliverable,
                        task: deliverables[i].task,
                        tags: deliverables[i].tags,
                    };
                    if (deliverables[i].templateDetails) {
                        deliverableToBePushed.templateId = deliverables[i].templateDetails?.id;
                    }
                    if (
                        deliverables[i].user_product_path &&
                        deliverables[i].user_product_path !== ''
                    ) {
                        deliverableToBePushed.userProductPath = deliverables[i].user_product_path;
                        deliverableToBePushed.adProductPhoto = deliverables[i].ad_product_photo
                            ? true
                            : false;
                    } else {
                        deliverableToBePushed.adProductPhoto = false;
                    }
                    payloadDeliverables.push(deliverableToBePushed);
                }

                const payload: IUpdateCampaignRequestPayload = {
                    campaignId: selectedCampaign.id,
                    name: campaignName,
                    objective: objective,
                    targetAudience: targetAudience,
                    keyMessage: keyMessages,
                    deliverables: payloadDeliverables,
                    brandId: activeBrand.id,
                };
                if (otherDetails.length > 0) {
                    payload.otherDetails = otherDetails;
                }

                const response = await updateCampaign(payload);
                if (response && 'data' in response) {
                    const exists = brandCampaigns.some((item) => item.id === response.data.id);
                    dispatch(
                        AddBrandCampaign(
                            setTemplateDetailsInCampaign(
                                response.data,
                                organizationTemplates ? organizationTemplates : [],
                            ),
                        ),
                    );
                    dispatch(
                        SetSelectedCampaign(
                            setTemplateDetailsInCampaign(
                                response.data,
                                organizationTemplates ? organizationTemplates : [],
                            ),
                        ),
                    );
                }
                displaySuccessToast('Campaign updated successfully.');
            }
        } catch (error) {
            displayErrorToast('Failed to update the campaign. Please try again.');
        }
    };

    const handleWriteCampaign = async () => {
        if (!activeBrand) return;
        if (generatingCampaign) {
            displayWarningToast('Campaign generation already in progress');
            return;
        }
        const payloadDeliverables: IWriteCampaignDeliverable[] = [];
        for (let i = 0; i < deliverables.length; i++) {
            const deliverableToBePushed: IWriteCampaignDeliverable = {
                assetType: deliverables[i].asset_type,
                platform: deliverables[i].platform,
                content: getCampaignContent(deliverables[i]),
                task: deliverables[i].task,
                tags: deliverables[i].tags,
            };
            if (deliverables[i].templateDetails) {
                deliverableToBePushed.templateId = deliverables[i].templateDetails?.id;
            }
            payloadDeliverables.push(deliverableToBePushed);
        }

        const payload: IWriteCampaignRequestPayload = {
            userPrompt: campaignWriterText,
            objective: objective,
            targetAudience: targetAudience,
            keyMessage: keyMessages,
            deliverables: payloadDeliverables,
            brandId: activeBrand.id,
        };

        const response = await writeCampaign(payload);
        if (response && 'data' in response) {
            const data = response.data;
            if (data) {
                if (data.deliverables.length > 0) {
                    const tempDeliverables: ICampaignDeliverables[] = [];
                    for (let i = 0; i < data.deliverables.length; i++) {
                        const deliverableFromData = data.deliverables[i];
                        const tempDeliverable: ICampaignDeliverables = {
                            asset_type: deliverableFromData.assetType,
                            platform: deliverableFromData.platform,
                            content: deliverableFromData.content,
                            deliverable_number: i + 1,
                            task: deliverableFromData.task,
                            tags: deliverableFromData.tags,
                            templateDetails: null,
                            user_product_path: '',
                        };
                        const tempTemplateDetails = getTemplateFromId(
                            deliverableFromData.templateId,
                            organizationTemplates,
                        );
                        if (tempTemplateDetails) {
                            tempDeliverable.templateDetails = tempTemplateDetails;
                            tempDeliverable.template = tempTemplateDetails.id;
                        }
                        tempDeliverables.push(tempDeliverable);
                    }
                    setDeliverables(tempDeliverables);
                }
                if (data.objective.length > 0) setObjective(data.objective);
                if (data.targetAudience.length > 0) setTargetAudience(data.targetAudience);
                if (data.key_messages.length > 0) setKeyMessages(data.key_messages);

                if (deliverableSectionRef.current) {
                    deliverableSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }
    };

    useEffect(() => {
        if (selectedCampaign) {
            oldCampaignPreTask();
        } else {
            newCampaignPreTask();
        }
    }, [currentlyWorkingSession, selectedCampaign, organizationTemplates]);

    return (
        <div className="tw-w-full">
            {adFromUrlActive && adFromUrlLoading ? (
                <div className="tw-w-full tw-h-80 tw-mt-5 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-6">
                    <div className="tw-relative">
                        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                            <div className="tw-w-20 tw-h-20 tw-rounded-full tw-border-4 tw-border-purple-100 tw-border-t-transparent tw-animate-spin"></div>
                        </div>
                        <div className="tw-w-28 tw-h-28 tw-rounded-full tw-bg-purple-50 tw-flex tw-items-center tw-justify-center">
                            <i className="ph-bold ph-calendar-plus tw-text-4xl tw-text-purple-100"></i>
                        </div>
                    </div>
                    <div className="tw-text-center tw-max-w-md">
                        <h4 className="tw-text-lg tw-font-medium tw-text-gray-800 tw-mb-2">
                            Creating Your Content Campaign
                        </h4>
                        <p className="tw-text-gray-600">
                            I'm tailoring a perfect campaign for your content. You'll be able to
                            customize it when it's ready.
                        </p>
                    </div>
                    <div className="tw-flex tw-gap-x-2 tw-items-center tw-text-purple-100">
                        <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-purple-100 tw-animate-pulse"></div>
                        <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-purple-100 tw-animate-pulse tw-animation-delay-300"></div>
                        <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-purple-100 tw-animate-pulse tw-animation-delay-600"></div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="tw-w-full tw-flex tw-items-center tw-justify-between tw-mb-6">
                        <div className="tw-flex tw-items-center tw-gap-x-6">
                            {isEditingCampaignName ? (
                                <Form.Control
                                    type="text"
                                    value={campaignName}
                                    onChange={handleCampaignNameChange}
                                    onBlur={handleCampaignNameBlur}
                                    onKeyDown={handleCampaignNameKeyDown}
                                    autoFocus
                                    className="p-2"
                                />
                            ) : (
                                <div className="tw-flex tw-items-center tw-gap-x-2">
                                    <div
                                        className="tw-font-medium tw-cursor-pointer tw-text-lg"
                                        onClick={() => setIsEditingCampaignName(true)}
                                    >
                                        {campaignName}
                                    </div>
                                    <i
                                        className="ph ph-pencil-simple lg tw-cursor-pointer"
                                        onClick={() => setIsEditingCampaignName(true)}
                                    ></i>
                                </div>
                            )}
                            {!isEditingCampaignName && !generatingCampaign && !adFromUrlActive && (
                                <div
                                    className="tw-underline tw-text-blue-200 tw-cursor-pointer tw-flex-1"
                                    onClick={() => setIsSwitching(true)}
                                >
                                    Switch Plan
                                </div>
                            )}
                        </div>
                        <div>
                            {!generatingCampaign && (!selectedCampaign || isCampaignEdited()) ? (
                                <>
                                    {!selectedCampaign ? (
                                        <Button
                                            variant="primary"
                                            onClick={handleSaveCampaign}
                                            disabled={createCampaignLoading}
                                        >
                                            Save
                                            {createCampaignLoading && (
                                                <Spinner
                                                    as="span"
                                                    style={{ width: '1.5rem', height: '1.5rem' }}
                                                />
                                            )}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="primary"
                                            onClick={handleUpdateCampaign}
                                            disabled={updateCampaignLoading}
                                        >
                                            Update
                                            {updateCampaignLoading && (
                                                <Spinner
                                                    as="span"
                                                    style={{ width: '1.5rem', height: '1.5rem' }}
                                                />
                                            )}
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {selectedCampaign && selectedCampaign.id && (
                                        <div className="tw-relative tw-inline-block">
                                            {adFromUrlActive && (
                                                <>
                                                    <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none">
                                                        <span className="tw-absolute tw-rounded-full tw-border-[5px] tw-border-[#60A5FA] tw-opacity-70 tw-animate-ping tw-w-16 tw-h-16"></span>
                                                        <span className="tw-absolute tw-rounded-full tw-border-[5px] tw-border-[#60A5FA] tw-opacity-60 tw-animate-ping tw-w-16 tw-h-16 tw-delay-[300ms]"></span>
                                                        <span className="tw-absolute tw-rounded-full tw-border-[5px] tw-border-[#60A5FA] tw-opacity-50 tw-animate-ping tw-w-16 tw-h-16 tw-delay-[600ms]"></span>
                                                    </span>
                                                </>
                                            )}

                                            <Button
                                                variant="primary"
                                                onClick={approveAndGenerateCampaign}
                                                disabled={generatingCampaign}
                                                className="tw-z-10 tw-relative"
                                            >
                                                Approve & Generate
                                                {generatingCampaign && (
                                                    <Spinner
                                                        as="span"
                                                        style={{
                                                            width: '1.5rem',
                                                            height: '1.5rem',
                                                        }}
                                                    />
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="tw-w-full tw-rounded-sm tw-py-3 tw-px-4 tw-border tw-border-blue-50 tw-shadow-sm">
                        <div
                            className="tw-w-full tw-flex tw-items-center tw-justify-between tw-cursor-pointer"
                            onClick={toggleCampaignDetailsCollapsed}
                        >
                            <div className="tw-flex tw-items-center tw-gap-x-3">
                                <i className="ph-bold ph-faders-horizontal lg"></i>
                                <span className="tw-text-base">Campaign Details</span>
                            </div>
                            {isCampaignDetailsCollapsed ? (
                                <i className="ph-bold ph-caret-down lg tw-cursor-pointer"></i>
                            ) : (
                                <i className="ph-bold ph-caret-up lg tw-cursor-pointer"></i>
                            )}
                        </div>
                        {!isCampaignDetailsCollapsed && (
                            <div className="tw-w-full tw-mt-6 tw-pl-4">
                                <div className="tw-border tw-border-gray-200 tw-rounded-md">
                                    <div className="tw-py-2 tw-px-3 tw-border-b tw-border-gray-200 tw-bg-gray-100">
                                        Description
                                    </div>
                                    <div className="tw-w-full">
                                        <Form.Control
                                            as="textarea"
                                            id={'campaignWriter'}
                                            value={campaignWriterText}
                                            onChange={(e) => setCampaignWriterText(e.target.value)}
                                            placeholder={
                                                "Write a description to generate campaign's details"
                                            }
                                            className="no-scroll tw-w-full tw-overflow-hidden !tw-rounded-t-none !tw-rounded-b-md"
                                            required={false}
                                            autoFocus={true}
                                        />
                                    </div>
                                </div>
                                <div className="tw-w-full tw-flex tw-items-center tw-justify-end mt-1">
                                    <Button
                                        variant="outline-primary"
                                        onClick={handleWriteCampaign}
                                        disabled={writeCampaignLoading}
                                    >
                                        Generate
                                        {writeCampaignLoading && (
                                            <Spinner as="span" className="tw-w-6 tw-h-6" />
                                        )}
                                    </Button>
                                </div>
                                <div className="tw-w-full tw-mt-6 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Objective:
                                    </span>
                                    {isEditingObjective ? (
                                        <CampaignTextArea
                                            onKeyPress={(e) =>
                                                handleKeyDown(e, () => setIsEditingObjective(false))
                                            }
                                            value={objective}
                                            placeholder={
                                                !selectedCampaign
                                                    ? "Your Campaign's Objectives"
                                                    : ''
                                            }
                                            className={'planObjective'}
                                            id={'planObjective'}
                                            onChange={(e) => setObjective(e.target.value)}
                                            rows={1}
                                            readOnly={false}
                                            required={true}
                                            onBlur={(e) => {
                                                setIsEditingObjective(false);
                                            }}
                                            autoFocus={true}
                                        />
                                    ) : (
                                        <span
                                            onClick={() => setIsEditingObjective(true)}
                                            style={{ cursor: 'pointer', width: '100%' }}
                                            className="text-justify"
                                        >
                                            {isEditingObjective
                                                ? objective
                                                : objective.length > 0
                                                  ? objective
                                                  : 'Enter objective'}
                                        </span>
                                    )}
                                </div>
                                <div className="tw-w-full tw-mt-2 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Target Audience:
                                    </span>
                                    {isEditingTargetAudience ? (
                                        <CampaignTextArea
                                            onKeyPress={(e) =>
                                                handleKeyDown(e, () =>
                                                    setIsEditingTargetAudience(false),
                                                )
                                            }
                                            value={targetAudience}
                                            placeholder={
                                                !selectedCampaign ? 'Your Target Audience' : ''
                                            }
                                            className={'planAudience'}
                                            id={'planAudience'}
                                            onChange={(e) => setTargetAudience(e.target.value)}
                                            rows={1}
                                            readOnly={false}
                                            required={true}
                                            onBlur={(e) => {
                                                setIsEditingTargetAudience(false);
                                            }}
                                            autoFocus={true}
                                        />
                                    ) : (
                                        <p
                                            onClick={() => setIsEditingTargetAudience(true)}
                                            style={{ cursor: 'pointer', width: '100%' }}
                                            className="text-justify"
                                        >
                                            {isEditingTargetAudience
                                                ? targetAudience
                                                : targetAudience.length > 0
                                                  ? targetAudience
                                                  : 'Enter target audience'}
                                        </p>
                                    )}
                                </div>
                                <div className="tw-w-full tw-mt-2 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Key Message:
                                    </span>
                                    {isEditingKeyMessages ? (
                                        <CampaignTextArea
                                            onKeyPress={(e) =>
                                                handleKeyDown(e, () =>
                                                    setIsEditingKeyMessages(false),
                                                )
                                            }
                                            value={keyMessages}
                                            placeholder={
                                                !selectedCampaign
                                                    ? 'Key message for your campaign'
                                                    : ''
                                            }
                                            className={'planMessage'}
                                            id={'planMessage'}
                                            onChange={(e) => setKeyMessages(e.target.value)}
                                            rows={1}
                                            readOnly={false}
                                            required={true}
                                            onBlur={(e) => {
                                                setIsEditingKeyMessages(false);
                                            }}
                                            autoFocus={true}
                                        />
                                    ) : (
                                        <p
                                            onClick={() => setIsEditingKeyMessages(true)}
                                            style={{ cursor: 'pointer', width: '100%' }}
                                            className="text-justify"
                                        >
                                            {isEditingKeyMessages
                                                ? keyMessages
                                                : keyMessages.length > 0
                                                  ? keyMessages
                                                  : 'Enter key message'}
                                        </p>
                                    )}
                                </div>
                                <div className="tw-w-full tw-mt-2 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Other Guidance:
                                    </span>
                                    {isEditingOtherDetails ? (
                                        <CampaignTextArea
                                            onKeyPress={(e) =>
                                                handleKeyDown(e, () =>
                                                    setIsEditingOtherDetails(false),
                                                )
                                            }
                                            value={otherDetails}
                                            placeholder={
                                                !selectedCampaign ? 'Any other guidance' : ''
                                            }
                                            className={'planOtherDetails'}
                                            id={'planOtherDetails flex-grow-1'}
                                            onChange={(e) => setOtherDetails(e.target.value)}
                                            rows={1}
                                            readOnly={false}
                                            required={true}
                                            onBlur={(e) => {
                                                setIsEditingOtherDetails(false);
                                            }}
                                            autoFocus={true}
                                        />
                                    ) : (
                                        <p
                                            onClick={() => setIsEditingOtherDetails(true)}
                                            style={{ cursor: 'pointer', width: '100%' }}
                                            className="text-justify"
                                        >
                                            {isEditingOtherDetails
                                                ? otherDetails
                                                : otherDetails.length > 0
                                                  ? otherDetails
                                                  : 'Enter other guidance'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="tw-w-full" ref={deliverableSectionRef}>
                        <DeliverableSection
                            deliverables={deliverables}
                            setDeliverables={setDeliverables}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default PlanEdit;
